"use client";

import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { de, enGB, Locale } from "date-fns/locale";
import {
  Popover,
  PopoverAction,
  PopoverContent,
  DatePicker,
  Label,
} from "keep-react";
import { Calendar } from "@phosphor-icons/react";

const locales: Record<string, Locale> = {
  de: de,
  en: enGB,
};

interface BirthdayInputProps {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  defaultValue?: Date | string | null;
}

export const BirthdayInput = ({ 
  name, 
  control, 
  label, 
  required = false, 
  disabled = false, 
  error,
  defaultValue 
}: BirthdayInputProps) => {
  const { t, i18n } = useTranslation();
  const currentLocale = locales[i18n.language] || locales.de;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // Parse field value to Date
        const parseValue = (value: Date | string | null) => {
          if (!value) return undefined;
          if (value instanceof Date) return value;
          if (typeof value === "string") {
            const date = new Date(value);
            return isNaN(date.getTime()) ? undefined : date;
          }
          return undefined;
        };

        const currentValue = parseValue(field.value || defaultValue);

        return (
          <div className="space-y-2">
            {label && (
              <Label className="text-slate-600 font-medium text-sm">
                {label}
                {required && <span className="text-red-500">*</span>}
              </Label>
            )}
            
            <Popover>
              <PopoverAction asChild>
                <button
                  type="button"
                  disabled={disabled}
                  className={`w-full justify-start gap-2 text-metal-900 opacity-60 border h-11 text-body-2 flex items-center leading-6 py-2 px-3 rounded-lg ${
                    error ? "border-red-500" : "border-gray-200"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Calendar
                    size={20}
                    className={error ? "text-red-500" : "text-metal-400"}
                  />
                  {currentValue ? (
                    format(currentValue, t("register.formats.date", "dd.MM.yyyy"), {
                      locale: currentLocale,
                    })
                  ) : (
                    <span className={error ? "text-red-500" : ""}>
                      {t("register.placeholders.dateOfBirth", "Geburtsdatum")}
                    </span>
                  )}
                </button>
              </PopoverAction>
              
              <PopoverContent align="start" className="max-w-min border-0">
                <DatePicker
                  dayShape="circle"
                  mode="single"
                  captionLayout="dropdown-buttons"
                  selected={currentValue}
                  onSelect={(date) => {
                    if (date) {
                      field.onChange(date);
                      setTimeout(() => field.onBlur(), 100);
                    }
                  }}
                  showOutsideDays={true}
                  fromYear={1900}
                  toYear={new Date().getFullYear() - 18}
                  defaultMonth={new Date(new Date().getFullYear() - 18, 0)}
                  locale={currentLocale}
                  formatters={{
                    formatMonthCaption: (date) => {
                      return t("date.months." + date.getMonth(), (date.getMonth() + 1).toString());
                    },
                    formatYearCaption: (date) => {
                      return date.getFullYear().toString();
                    }
                  }}
                  components={{
                    Dropdown: ({ value, onChange, children, ...props }) => {
                      const isMonth = props.name === "months";
                      const label = isMonth ? t("date.labels.month", "Monat") : t("date.labels.year", "Jahr");
                      
                      return (
                        <div className="flex flex-col items-center mx-1">
                          <label className="text-xs text-gray-500 mb-1">{label}</label>
                          <select
                            value={value}
                            onChange={onChange}
                            className="p-1 text-sm font-medium bg-white border border-gray-200 rounded-md cursor-pointer"
                          >
                            {children}
                          </select>
                        </div>
                      );
                    }
                  }}
                  classNames={{
                    caption: "flex justify-between px-1",
                    caption_label: "hidden",
                    caption_dropdowns: "flex gap-1 grow justify-center",
                    dropdown: "p-1 text-sm font-medium",
                    dropdown_month: "text-sm font-medium",
                    dropdown_year: "text-sm font-medium",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent text-gray-600 hover:bg-gray-100",
                    table: "w-full border-collapse border-spacing-0",
                    head_row: "flex",
                    head_cell: "w-9 font-normal text-sm text-gray-500",
                    row: "flex w-full mt-2",
                    cell: "text-gray-600 rounded-md h-9 w-9 text-center text-sm relative p-0 focus-within:relative focus-within:z-20",
                    day: "h-9 w-9 p-0 font-normal",
                    day_selected: "bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white",
                    day_today: "bg-gray-100 text-gray-900",
                    day_outside: "text-gray-400 opacity-50"
                  }}
                />
              </PopoverContent>
            </Popover>

            {error && (
              <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
                {error}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};